// Waitlist Management Service for BookAPT
// Handles waitlist functionality for fully booked time slots

import { Platform } from 'react-native';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

export interface WaitlistEntry {
  id: string;
  customerId: string;
  businessId: string;
  serviceId: string;
  serviceName: string;
  requestedDate: string;
  requestedTime: string;
  duration: number;
  price: number;
  position: number;
  estimatedWaitTime: number; // in minutes
  status: 'waiting' | 'notified' | 'booked' | 'expired' | 'cancelled';
  customerData: {
    name: string;
    phone: string;
    email: string;
    preferences: string[];
  };
  businessData: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  serviceData: {
    name: string;
    duration: number;
    price: number;
    description: string;
    requirements: string[];
  };
  createdAt: string;
  notifiedAt?: string;
  bookedAt?: string;
  expiredAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
}

export interface WaitlistNotification {
  id: string;
  waitlistEntryId: string;
  customerId: string;
  businessId: string;
  type: 'slot_available' | 'position_update' | 'expiry_warning' | 'cancelled';
  message: string;
  actionRequired: boolean;
  actionText?: string;
  actionUrl?: string;
  sentAt: string;
  readAt?: string;
}

export interface WaitlistAnalytics {
  totalEntries: number;
  activeEntries: number;
  convertedEntries: number;
  expiredEntries: number;
  cancelledEntries: number;
  averageWaitTime: number;
  conversionRate: number;
  mostRequestedServices: { serviceName: string; count: number }[];
  peakWaitTimes: { time: string; count: number }[];
}

class WaitlistService {
  private readonly WAITLIST_ENTRIES_KEY = 'waitlist_entries';
  private readonly WAITLIST_NOTIFICATIONS_KEY = 'waitlist_notifications';
  private readonly WAITLIST_ANALYTICS_KEY = 'waitlist_analytics';

  // Add customer to waitlist
  async addToWaitlist(
    waitlistData: {
      customerId: string;
      businessId: string;
      serviceId: string;
      serviceName: string;
      requestedDate: string;
      requestedTime: string;
      duration: number;
      price: number;
      customerData: {
        name: string;
        phone: string;
        email: string;
        preferences: string[];
      };
      businessData: {
        name: string;
        address: string;
        phone: string;
        email: string;
      };
      serviceData: {
        name: string;
        duration: number;
        price: number;
        description: string;
        requirements: string[];
      };
    }
  ): Promise<WaitlistEntry | null> {
    try {
      // Check if customer is already on waitlist for this service/time
      const existingEntries = await this.getWaitlistEntries(waitlistData.businessId);
      const existingEntry = existingEntries.find(entry => 
        entry.customerId === waitlistData.customerId &&
        entry.serviceId === waitlistData.serviceId &&
        entry.requestedDate === waitlistData.requestedDate &&
        entry.requestedTime === waitlistData.requestedTime &&
        entry.status === 'waiting'
      );

      if (existingEntry) {
        throw new Error('You are already on the waitlist for this service');
      }

      // Get next position
      const nextPosition = await this.getNextWaitlistPosition(waitlistData.businessId, waitlistData.serviceId);
      
      // Calculate estimated wait time
      const estimatedWaitTime = await this.calculateEstimatedWaitTime(
        waitlistData.businessId,
        waitlistData.serviceId,
        nextPosition
      );

      const waitlistEntry: WaitlistEntry = {
        id: `waitlist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        customerId: waitlistData.customerId,
        businessId: waitlistData.businessId,
        serviceId: waitlistData.serviceId,
        serviceName: waitlistData.serviceName,
        requestedDate: waitlistData.requestedDate,
        requestedTime: waitlistData.requestedTime,
        duration: waitlistData.duration,
        price: waitlistData.price,
        position: nextPosition,
        estimatedWaitTime,
        status: 'waiting',
        customerData: waitlistData.customerData,
        businessData: waitlistData.businessData,
        serviceData: waitlistData.serviceData,
        createdAt: new Date().toISOString()
      };

      // Save waitlist entry
      await this.saveWaitlistEntry(waitlistEntry);

      // Send confirmation notification
      await this.sendWaitlistNotification(waitlistEntry, 'added_to_waitlist');

      return waitlistEntry;
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      return null;
    }
  }

  // Get waitlist entries for business (Firestore first, then local fallback)
  async getWaitlistEntries(businessId: string): Promise<WaitlistEntry[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedEntries: WaitlistEntry[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const firestoreMod = getFirestoreFunctions();
          const db = getWebDb();
          if (db && firestoreMod) {
            const { collection, query, where, getDocs } = firestoreMod;
            // Option 1: Query waitlist_entries collection with businessId filter
            const waitlistQuery = query(
              collection(db, 'waitlist_entries'),
              where('businessId', '==', businessId)
            );
            const waitlistSnapshot = await getDocs(waitlistQuery);
            loadedEntries = waitlistSnapshot.docs.map((snapDoc: QueryDocumentSnapshot<DocumentData>) => ({
              id: snapDoc.id,
              ...snapDoc.data()
            } as WaitlistEntry));
            
            // Also try business-specific collection
            try {
              const businessWaitlistQuery = query(
                collection(db, 'businesses', businessId, 'waitlist')
              );
              const businessWaitlistSnapshot = await getDocs(businessWaitlistQuery);
              const businessEntries = businessWaitlistSnapshot.docs.map((snapDoc: QueryDocumentSnapshot<DocumentData>) => ({
                id: snapDoc.id,
                ...snapDoc.data()
              } as WaitlistEntry));
              
              // Merge and deduplicate
              const allLoaded = [...loadedEntries, ...businessEntries];
              const uniqueMap = new Map();
              allLoaded.forEach(entry => {
                if (!uniqueMap.has(entry.id)) {
                  uniqueMap.set(entry.id, entry);
                }
              });
              loadedEntries = Array.from(uniqueMap.values());
            } catch (e) {
              // Business-specific collection might not exist, that's OK
            }
            
            loadedFromFirestore = true;
            console.log(`✅ WaitlistService: Loaded ${loadedEntries.length} waitlist entries from Firestore`);

            // Sync to local storage
            const { default: SimpleStorage } = await import('../utils/simpleStorage');
            await SimpleStorage.setItem(this.WAITLIST_ENTRIES_KEY, JSON.stringify(loadedEntries));
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  WaitlistService: Could not load entries from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
        const { default: SimpleStorage } = await import('../utils/simpleStorage');
        await SimpleStorage.forceInitialize();
        const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
        if (entriesJson) {
          const allEntries: WaitlistEntry[] = JSON.parse(entriesJson);
          loadedEntries = allEntries.filter(entry => entry.businessId === businessId);
          console.log(`📋 WaitlistService: Loaded ${loadedEntries.length} waitlist entries from local storage`);
        }
      } else {
        // Filter by businessId if loaded from Firestore
        loadedEntries = loadedEntries.filter(entry => entry.businessId === businessId);
      }
      
      return loadedEntries;
    } catch (error) {
      console.error('Error getting waitlist entries:', error);
      return [];
    }
  }

  // Get waitlist entries for customer
  async getCustomerWaitlistEntries(customerId: string): Promise<WaitlistEntry[]> {
    try {
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
      const allEntries: WaitlistEntry[] = entriesJson ? JSON.parse(entriesJson) : [];
      return allEntries.filter(entry => entry.customerId === customerId);
    } catch (error) {
      console.error('Error getting customer waitlist entries:', error);
      return [];
    }
  }

  // Notify next customer when slot becomes available
  async notifyNextCustomer(
    businessId: string,
    serviceId: string,
    availableDate: string,
    availableTime: string
  ): Promise<WaitlistEntry | null> {
    try {
      const waitlistEntries = await this.getWaitlistEntries(businessId);
      const relevantEntries = waitlistEntries.filter(entry => 
        entry.serviceId === serviceId &&
        entry.status === 'waiting' &&
        entry.requestedDate === availableDate
      );

      if (relevantEntries.length === 0) {
        return null;
      }

      // Sort by position and creation time
      relevantEntries.sort((a, b) => {
        if (a.position !== b.position) {
          return a.position - b.position;
        }
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });

      const nextEntry = relevantEntries[0];
      
      // Update entry status
      nextEntry.status = 'notified';
      nextEntry.notifiedAt = new Date().toISOString();
      await this.updateWaitlistEntry(nextEntry);

      // Send notification
      await this.sendWaitlistNotification(nextEntry, 'slot_available', {
        availableDate,
        availableTime
      });

      return nextEntry;
    } catch (error) {
      console.error('Error notifying next customer:', error);
      return null;
    }
  }

  // Book appointment from waitlist
  async bookFromWaitlist(
    waitlistEntryId: string,
    appointmentData: any
  ): Promise<boolean> {
    try {
      const waitlistEntry = await this.getWaitlistEntry(waitlistEntryId);
      if (!waitlistEntry) {
        return false;
      }

      // Update waitlist entry status
      waitlistEntry.status = 'booked';
      waitlistEntry.bookedAt = new Date().toISOString();
      await this.updateWaitlistEntry(waitlistEntry);

      // Create appointment
      const { appointmentManager } = await import('../utils/appointmentManager');
      await appointmentManager.saveAppointment(appointmentData);

      // Send confirmation notification
      await this.sendWaitlistNotification(waitlistEntry, 'appointment_booked');

      // Update positions of remaining entries
      await this.updateWaitlistPositions(waitlistEntry.businessId, waitlistEntry.serviceId);

      return true;
    } catch (error) {
      console.error('Error booking from waitlist:', error);
      return false;
    }
  }

  // Cancel waitlist entry
  async cancelWaitlistEntry(
    waitlistEntryId: string,
    reason: string
  ): Promise<boolean> {
    try {
      const waitlistEntry = await this.getWaitlistEntry(waitlistEntryId);
      if (!waitlistEntry) {
        return false;
      }

      waitlistEntry.status = 'cancelled';
      waitlistEntry.cancelledAt = new Date().toISOString();
      waitlistEntry.cancellationReason = reason;
      await this.updateWaitlistEntry(waitlistEntry);

      // Send cancellation notification
      await this.sendWaitlistNotification(waitlistEntry, 'cancelled');

      // Update positions of remaining entries
      await this.updateWaitlistPositions(waitlistEntry.businessId, waitlistEntry.serviceId);

      return true;
    } catch (error) {
      console.error('Error cancelling waitlist entry:', error);
      return false;
    }
  }

  // Expire old waitlist entries
  async expireOldEntries(): Promise<number> {
    try {
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
      const allEntries: WaitlistEntry[] = entriesJson ? JSON.parse(entriesJson) : [];
      
      const now = new Date();
      const expiredEntries: WaitlistEntry[] = [];
      
      for (const entry of allEntries) {
        if (entry.status === 'waiting' || entry.status === 'notified') {
          const entryDate = new Date(entry.requestedDate);
          const daysDiff = (now.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24);
          
          // Expire entries older than 7 days
          if (daysDiff > 7) {
            entry.status = 'expired';
            entry.expiredAt = new Date().toISOString();
            expiredEntries.push(entry);
          }
        }
      }
      
      if (expiredEntries.length > 0) {
        await SimpleStorage.setItem(this.WAITLIST_ENTRIES_KEY, JSON.stringify(allEntries));
        
        // Send expiry notifications
        for (const entry of expiredEntries) {
          await this.sendWaitlistNotification(entry, 'expired');
        }
      }
      
      return expiredEntries.length;
    } catch (error) {
      console.error('Error expiring old entries:', error);
      return 0;
    }
  }

  // Get waitlist analytics
  async getWaitlistAnalytics(businessId: string): Promise<WaitlistAnalytics> {
    try {
      const waitlistEntries = await this.getWaitlistEntries(businessId);
      
      const analytics: WaitlistAnalytics = {
        totalEntries: waitlistEntries.length,
        activeEntries: waitlistEntries.filter(entry => entry.status === 'waiting').length,
        convertedEntries: waitlistEntries.filter(entry => entry.status === 'booked').length,
        expiredEntries: waitlistEntries.filter(entry => entry.status === 'expired').length,
        cancelledEntries: waitlistEntries.filter(entry => entry.status === 'cancelled').length,
        averageWaitTime: this.calculateAverageWaitTime(waitlistEntries),
        conversionRate: this.calculateConversionRate(waitlistEntries),
        mostRequestedServices: this.getMostRequestedServices(waitlistEntries),
        peakWaitTimes: this.getPeakWaitTimes(waitlistEntries)
      };

      return analytics;
    } catch (error) {
      console.error('Error getting waitlist analytics:', error);
      return {
        totalEntries: 0,
        activeEntries: 0,
        convertedEntries: 0,
        expiredEntries: 0,
        cancelledEntries: 0,
        averageWaitTime: 0,
        conversionRate: 0,
        mostRequestedServices: [],
        peakWaitTimes: []
      };
    }
  }

  // Helper methods
  private async getNextWaitlistPosition(businessId: string, serviceId: string): Promise<number> {
    try {
      const waitlistEntries = await this.getWaitlistEntries(businessId);
      const serviceEntries = waitlistEntries.filter(entry => 
        entry.serviceId === serviceId && 
        entry.status === 'waiting'
      );
      
      return serviceEntries.length + 1;
    } catch (error) {
      console.error('Error getting next waitlist position:', error);
      return 1;
    }
  }

  private async calculateEstimatedWaitTime(
    businessId: string,
    serviceId: string,
    position: number
  ): Promise<number> {
    try {
      // In a real app, this would use historical data
      // For demo purposes, estimate 30 minutes per position
      return position * 30;
    } catch (error) {
      console.error('Error calculating estimated wait time:', error);
      return position * 30;
    }
  }

  private async getWaitlistEntry(waitlistEntryId: string): Promise<WaitlistEntry | null> {
    try {
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
      const allEntries: WaitlistEntry[] = entriesJson ? JSON.parse(entriesJson) : [];
      return allEntries.find(entry => entry.id === waitlistEntryId) || null;
    } catch (error) {
      console.error('Error getting waitlist entry:', error);
      return null;
    }
  }

  private async saveWaitlistEntry(waitlistEntry: WaitlistEntry): Promise<void> {
    try {
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
      const allEntries: WaitlistEntry[] = entriesJson ? JSON.parse(entriesJson) : [];
      allEntries.push(waitlistEntry);
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.WAITLIST_ENTRIES_KEY, JSON.stringify(allEntries));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const firestoreMod = getFirestoreFunctions();
          const db = getWebDb();
          if (db && firestoreMod) {
            const { doc, setDoc } = firestoreMod;
            // Save to main waitlist_entries collection
            await setDoc(doc(db, 'waitlist_entries', waitlistEntry.id), {
              ...waitlistEntry,
              updatedAt: new Date().toISOString(),
            });
            
            // Also save to business-specific collection for faster queries
            await setDoc(doc(db, 'businesses', waitlistEntry.businessId, 'waitlist', waitlistEntry.id), {
              ...waitlistEntry,
              updatedAt: new Date().toISOString(),
            });
            
            console.log(`✅ WaitlistService: Waitlist entry saved to Firestore: ${waitlistEntry.id}`);
          }
        } catch (firestoreError: any) {
          console.error('❌ WaitlistService: Error saving waitlist entry to Firestore:', firestoreError);
          console.warn('⚠️  Waitlist entry saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving waitlist entry:', error);
    }
  }

  private async updateWaitlistEntry(waitlistEntry: WaitlistEntry): Promise<void> {
    try {
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const entriesJson = await SimpleStorage.getItem(this.WAITLIST_ENTRIES_KEY);
      const allEntries: WaitlistEntry[] = entriesJson ? JSON.parse(entriesJson) : [];
      const index = allEntries.findIndex(entry => entry.id === waitlistEntry.id);
      
      if (index !== -1) {
        allEntries[index] = waitlistEntry;
        
        // Save to local storage (for offline access)
        await SimpleStorage.setItem(this.WAITLIST_ENTRIES_KEY, JSON.stringify(allEntries));
        
        // Also update in Firestore (for cross-browser/device access)
        if (Platform.OS === 'web') {
          try {
            const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
            const firestoreMod = getFirestoreFunctions();
            const db = getWebDb();
            if (db && firestoreMod) {
              const { doc, updateDoc } = firestoreMod;
              // Update in main waitlist_entries collection
              await updateDoc(doc(db, 'waitlist_entries', waitlistEntry.id), {
                ...waitlistEntry,
                updatedAt: new Date().toISOString(),
              });
              
              // Also update in business-specific collection
              await updateDoc(doc(db, 'businesses', waitlistEntry.businessId, 'waitlist', waitlistEntry.id), {
                ...waitlistEntry,
                updatedAt: new Date().toISOString(),
              });
              
              console.log(`✅ WaitlistService: Waitlist entry updated in Firestore: ${waitlistEntry.id}`);
            }
          } catch (firestoreError: any) {
            console.error('❌ WaitlistService: Error updating waitlist entry in Firestore:', firestoreError);
            // Don't throw - local update succeeded
          }
        }
      }
    } catch (error) {
      console.error('Error updating waitlist entry:', error);
    }
  }

  private async updateWaitlistPositions(businessId: string, serviceId: string): Promise<void> {
    try {
      const waitlistEntries = await this.getWaitlistEntries(businessId);
      const serviceEntries = waitlistEntries.filter(entry => 
        entry.serviceId === serviceId && 
        entry.status === 'waiting'
      );
      
      serviceEntries.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      
      for (let i = 0; i < serviceEntries.length; i++) {
        serviceEntries[i].position = i + 1;
        await this.updateWaitlistEntry(serviceEntries[i]);
      }
    } catch (error) {
      console.error('Error updating waitlist positions:', error);
    }
  }

  private async sendWaitlistNotification(
    waitlistEntry: WaitlistEntry,
    type: string,
    data?: any
  ): Promise<void> {
    try {
      const notification: WaitlistNotification = {
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        waitlistEntryId: waitlistEntry.id,
        customerId: waitlistEntry.customerId,
        businessId: waitlistEntry.businessId,
        type: type as any,
        message: this.generateNotificationMessage(type, waitlistEntry, data),
        actionRequired: type === 'slot_available',
        actionText: type === 'slot_available' ? 'Book Now' : undefined,
        sentAt: new Date().toISOString()
      };

      // Save notification
      const { default: SimpleStorage } = await import('../utils/simpleStorage');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const notificationsJson = await SimpleStorage.getItem(this.WAITLIST_NOTIFICATIONS_KEY);
      const allNotifications: WaitlistNotification[] = notificationsJson ? JSON.parse(notificationsJson) : [];
      allNotifications.push(notification);
      await SimpleStorage.setItem(this.WAITLIST_NOTIFICATIONS_KEY, JSON.stringify(allNotifications));

      // Send SMS/Email notification
      const { smsService } = await import('./smsService');
      await smsService.sendSMS(
        waitlistEntry.customerData.phone,
        notification.message,
        'general'
      );
    } catch (error) {
      console.error('Error sending waitlist notification:', error);
    }
  }

  private generateNotificationMessage(
    type: string,
    waitlistEntry: WaitlistEntry,
    data?: any
  ): string {
    switch (type) {
      case 'added_to_waitlist':
        return `Hi ${waitlistEntry.customerData.name}, you've been added to the waitlist for ${waitlistEntry.serviceName} on ${waitlistEntry.requestedDate}. You're position #${waitlistEntry.position}. We'll notify you when a slot becomes available.`;
      
      case 'slot_available':
        return `Hi ${waitlistEntry.customerData.name}, a slot is now available for ${waitlistEntry.serviceName} on ${data?.availableDate} at ${data?.availableTime}. Book now to secure your spot!`;
      
      case 'appointment_booked':
        return `Hi ${waitlistEntry.customerData.name}, your appointment for ${waitlistEntry.serviceName} has been confirmed! Thank you for using BookAPT.`;
      
      case 'cancelled':
        return `Hi ${waitlistEntry.customerData.name}, your waitlist entry for ${waitlistEntry.serviceName} has been cancelled. Reason: ${waitlistEntry.cancellationReason}`;
      
      case 'expired':
        return `Hi ${waitlistEntry.customerData.name}, your waitlist entry for ${waitlistEntry.serviceName} has expired. Please book a new appointment if you're still interested.`;
      
      default:
        return `Update regarding your waitlist entry for ${waitlistEntry.serviceName}.`;
    }
  }

  private calculateAverageWaitTime(waitlistEntries: WaitlistEntry[]): number {
    const completedEntries = waitlistEntries.filter(entry => entry.status === 'booked');
    if (completedEntries.length === 0) return 0;
    
    const totalWaitTime = completedEntries.reduce((sum, entry) => {
      const waitTime = new Date(entry.bookedAt || '').getTime() - new Date(entry.createdAt).getTime();
      return sum + (waitTime / (1000 * 60)); // Convert to minutes
    }, 0);
    
    return totalWaitTime / completedEntries.length;
  }

  private calculateConversionRate(waitlistEntries: WaitlistEntry[]): number {
    const totalEntries = waitlistEntries.length;
    const convertedEntries = waitlistEntries.filter(entry => entry.status === 'booked').length;
    
    return totalEntries > 0 ? (convertedEntries / totalEntries) * 100 : 0;
  }

  private getMostRequestedServices(waitlistEntries: WaitlistEntry[]): { serviceName: string; count: number }[] {
    const serviceCounts: { [key: string]: number } = {};
    
    waitlistEntries.forEach(entry => {
      serviceCounts[entry.serviceName] = (serviceCounts[entry.serviceName] || 0) + 1;
    });
    
    return Object.entries(serviceCounts)
      .map(([serviceName, count]) => ({ serviceName, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }

  private getPeakWaitTimes(waitlistEntries: WaitlistEntry[]): { time: string; count: number }[] {
    const timeCounts: { [key: string]: number } = {};
    
    waitlistEntries.forEach(entry => {
      const time = entry.requestedTime;
      timeCounts[time] = (timeCounts[time] || 0) + 1;
    });
    
    return Object.entries(timeCounts)
      .map(([time, count]) => ({ time, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }
}

export const waitlistService = new WaitlistService();
export default WaitlistService;

